import { useRef } from 'react';
import {
  StyledHeroSection,
  StyledHeroBody,
  StyledHeroBand,
  StyledHeroIdentity,
  StyledHero,
  StyledHeroContent,
  StyledHeroHeadline,
  StyledHeroTagline,
  StyledHeroCtaGroup,
  StyledHeroCta,
} from './Hero.styles';
import { useHero } from './useHero';
import { Container } from '@/components/common/Container';
import { Link } from 'react-router-dom';

export interface HeroSectionLink {
  label: string;
  summary: string;
  href: string;
}

export interface HeroProps {
  name: string;
  title: string;
  tagline: string;
  cta?: {
    label: string;
    href: string;
  };
  secondaryCta?: {
    label: string;
    href: string;
  };
  sections?: HeroSectionLink[];
}

export const Hero = ({
  name,
  title,
  tagline,
  cta,
  secondaryCta,
}: HeroProps) => {
  const { ctaRef, secondaryCtaRef } = useHero();
  const ctaGroupRef = useRef<HTMLDivElement>(null);

  return (
    <StyledHeroSection
      id="hero"
      data-testid="hero-section"
      ariaLabel="Hero"
      size="xl"
      background="default"
      fullViewport={true}
      composition="hero"
      verticalAlign="top"
    >
      <StyledHeroBand data-testid="hero-band" data-header-contrast="dark">
        <Container size="xl" padding="lg">
          <StyledHeroIdentity>
            <StyledHeroHeadline as="h1">
              <span>{name}</span>
            </StyledHeroHeadline>
            <StyledHeroTagline as="p" $variant="title">{title}</StyledHeroTagline>
          </StyledHeroIdentity>
        </Container>
      </StyledHeroBand>

      <StyledHeroBody size="xl" padding="lg" data-testid="hero-body">
        <StyledHero data-testid="hero-block">
          <StyledHeroContent>
            <StyledHeroTagline as="p" $variant="description">{tagline}</StyledHeroTagline>

            {(cta || secondaryCta) && (
              <StyledHeroCtaGroup ref={ctaGroupRef} data-testid="hero-cta-group">
                {cta && (
                  <StyledHeroCta
                    as={Link}
                    to={cta.href}
                    $variant="primary"
                    ref={ctaRef}
                    data-testid="cta-primary"
                  >
                    {cta.label}
                  </StyledHeroCta>
                )}
                {secondaryCta && (
                  <StyledHeroCta as={Link} to={secondaryCta.href} $variant="ghost" ref={secondaryCtaRef}>
                    {secondaryCta.label}
                  </StyledHeroCta>
                )}
              </StyledHeroCtaGroup>
            )}
          </StyledHeroContent>
        </StyledHero>
      </StyledHeroBody>
    </StyledHeroSection>
  );
};
